import { FC } from "react";
import { useRouter } from "next/router";
import { useAtomValue, useSetAtom } from "jotai";
import { Box, Button, Dialog, DialogActions, DialogContent, Typography } from "@mui/material";
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";
import { planPickerDrawerOpenState, userState } from "@/lib/store";
import useAppSubscriberUpgrade from "src/hooks/useAppSubscriberUpgrade";
import { UPGRADE_LOCATIONS, trackUpgradeClick } from "@/lib/tracking";
import { isOrtexApp } from "@/lib/utils";

// Dividend dates are open to everyone; amounts, chances, yields and the brief are part of ORTEX
// Advanced and above. Same CTA routing as the Alt Data and Index Rebalance unlock dialogs.
const CTA: Record<string, string> = {
    "ORTEX Visitor": "Create free account",
    "ORTEX Free": "Upgrade now",
    "ORTEX Basic": "Get Advanced",
};

/**
 * The Dividends section's upgrade dialog, opened by every Unlock button on its pages (the stock tab's too).
 * `forecasts` false (no forecast published for this caller, TED #2467): it sells what Advanced
 * unlocks today, never a forecast the reader would not get.
 */
const DividendsUnlockDialog: FC<{ open: boolean; onClose: () => void; forecasts?: boolean }> = ({ open, onClose, forecasts = true }) => {
    const user = useAtomValue(userState);
    const setPlanPickerOpen = useSetAtom(planPickerDrawerOpenState);
    const { handleAppSubscriberUpgrade } = useAppSubscriberUpgrade();
    const { push } = useRouter();

    const upgrade = () => {
        trackUpgradeClick(UPGRADE_LOCATIONS.DIVIDENDS_UNLOCK_DIALOG, { plan: user?.subscription_plan });
        onClose();
        if (!user || user.subscription_plan === "ORTEX Visitor") push("/login?register=true");
        else if (isOrtexApp()) setPlanPickerOpen(true);
        else handleAppSubscriberUpgrade();
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth aria-labelledby="dividends-unlock-title">
            <DialogContent>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
                    <Box sx={{ width: 40, height: 40, borderRadius: 1, display: "grid", placeItems: "center", backgroundColor: "primary.main", color: "primary.contrastText" }}>
                        <PaidOutlinedIcon />
                    </Box>
                    <Typography id="dividends-unlock-title" variant="h2" sx={{ fontWeight: 700 }}>
                        {forecasts ? "Dividend forecasts are part of ORTEX Advanced" : "Dividend amounts are part of ORTEX Advanced"}
                    </Typography>
                </Box>
                <Typography variant="body1" sx={{ mb: 1.5 }}>
                    {forecasts
                        ? "The forecast amount and its range, the chance of a raise, hold or cut, what the options market implies and the morning brief, for every dividend payer ORTEX covers."
                        : "Declared and past dividend amounts, yields, payout cover, borrowing around the record date, the income planner and dividend alerts, for every dividend payer ORTEX covers."}
                </Typography>
                <Typography
                    variant="body2"
                    sx={{
                        color: "text.secondary",
                    }}
                >
                    Dates stay open to everyone. Included with ORTEX Advanced, Professional and Institutional subscriptions.
                </Typography>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
                <Button variant="text" onClick={onClose}>
                    Maybe later
                </Button>
                <Button variant="contained" onClick={upgrade}>
                    {CTA[user?.subscription_plan] || "Upgrade"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default DividendsUnlockDialog;
